<script setup>
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/stores/authStore.js'
import { userApi } from '@/api/User/UserApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import InputText from '@/components/Forms/InputText.vue'
import CheckBox from '@/components/Forms/CheckBox.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { Format as FormatUtils } from '@/models/utils/FormatUtils.js'
import Validation from '@/models/utils/Validation.js'
import ValidationError from '@/components/Forms/InputError.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'

const toaster = new Toaster()

/**
 * Validation object to add/remove validation errors
 * @type {[Validation] extends [Ref] ? IfAny<Validation, Ref<Validation>, Validation> : Ref<UnwrapRef<Validation>, UnwrapRef<Validation> | Validation>}
 */
const validation = ref(new Validation())

const tenant = ref(null)
const tenantSettings = ref({})
const isSaving = ref(false)
const isLoading = ref(false)
const showDiscardChangesModal = ref(false)
const selectedPasswordExpirationOption = ref('expire_days')
const passwordExpirationDays = ref(90)
const loginWarningDays = ref(7)
const passwordChangeDays = ref(14)
const isReadOnly = computed(() => {
	return selectedPasswordExpirationOption.value === 'expire_never'
})
const selectedReminderDays = ref([])
const reminderDaysText = computed(() => {
	const formatter = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' })
	return formatter.format(selectedReminderDays.value.map(day => day.toString()))
})
const nonEmptyNumberFilter = (event, callback) => {
	if (event.target.value === '') {
		event.target.value = '0'
		callback(event.target.value)
	}
}
const beforeEditNumber = (event, callback) => {
	if (event.target.value === '0' && event.data) {
		event.target.value = ''
		callback(event.target.value)
	}
}

const emit = defineEmits(['dismiss'])

onMounted(() => {
	loadSettings()
})

function loadSettings()
{
	isLoading.value = true

	userApi.getTenantById(useAuthStore().getAuth.user().tenant_id, (tenantData) => {
		tenant.value = tenantData
		tenantSettings.value = JSON.parse(tenant.value.settings_json)

		selectedPasswordExpirationOption.value = tenantSettings.value.passwords.expiration_enabled.value ? 'expire_days' : 'expire_never'
		passwordExpirationDays.value = tenantSettings.value.passwords.expiration_days.value
		loginWarningDays.value = tenantSettings.value.passwords.login_warning_days.value
		passwordChangeDays.value = tenantSettings.value.passwords.change_days.value
		selectedReminderDays.value = [...tenantSettings.value.passwords.reminder_email_days.value]
		selectedReminderDays.value.sort((a, b) => b - a)

		isLoading.value = false
	})
}

/**
 * Save settings.
 * Note, SanitizeInput middleware will strip out html/script tags.
 */
function save()
{
	if (!validate()) return

	const settings = {
		passwords: {
			expiration_enabled: selectedPasswordExpirationOption.value === 'expire_days',
			expiration_days: passwordExpirationDays.value,
			login_warning_days: loginWarningDays.value,
			reminder_email_days: selectedReminderDays.value,
			change_days: passwordChangeDays.value,
		}
	}

	isSaving.value = true
	userApi.updateTenantSettings({ settings: settings },
		() => {
			isSaving.value = false
			toaster.success('Password expiration settings successfully updated!')

			emit('dismiss', true)
		}, (e) => {
			validation.value.errors = e.showErrorBanner()
			isSaving.value = false
		})
}

function validate()
{
	if (selectedPasswordExpirationOption.value === 'expire_never') return true

	const passwordExpirationDaysMinimum = tenantSettings.value.passwords.expiration_days.minimum
	const loginWarningDaysMinimum = tenantSettings.value.passwords.login_warning_days.minimum
	const passwordChangeDaysMinimum = tenantSettings.value.passwords.change_days.minimum
	const maxReminderEmail = Math.max(...selectedReminderDays.value)

	const toCompare = [
		{ current: passwordExpirationDays.value, minimum: passwordExpirationDaysMinimum, compareType: 'less', fieldName: 'expiration_days', message: `Password expiration days is required and must be at least ${passwordExpirationDaysMinimum}.`},
		{ current: loginWarningDays.value, minimum: loginWarningDaysMinimum, compareType: 'less', fieldName: 'login_warning_days', message: `Warning email days is required and must be at least ${loginWarningDaysMinimum}.`},
		{ current: passwordChangeDays.value, minimum: passwordChangeDaysMinimum, compareType: 'less', fieldName: 'change_days', message: `Password change days is required and must be at least ${passwordChangeDaysMinimum}.`},
		{ current: passwordExpirationDays.value, minimum: maxReminderEmail, compareType: 'less_equal', fieldName: 'expiration_days', message: `Password expiration days must be greater than expiration email ${maxReminderEmail}.`},
		{ current: passwordExpirationDays.value, minimum: loginWarningDays.value, compareType: 'less_equal', fieldName: 'expiration_days', message: `Password expiration days must be greater than log-in warning ${loginWarningDays.value}.`},
	]

	validation.value.removeAll()
	toCompare.forEach((setting) => {
		if (!setting.current || (
			(setting.compareType === 'less' && parseInt(setting.current) < setting.minimum) ||
			(setting.compareType === 'less_equal' && parseInt(setting.current) <= setting.minimum))
		)
		{
			validation.value.add('settings.passwords.' + setting.fieldName, setting.message)
		}
	})

	return validation.value.count() === 0
}

function setExpirationDays(days)
{
	passwordExpirationDays.value = days
}

function updateReminderDay(isChecked, day)
{
	if (isChecked)
	{
		if (!selectedReminderDays.value.includes(day))
		{
			selectedReminderDays.value.push(day)
		}
	}
	else
	{
		selectedReminderDays.value = selectedReminderDays.value.filter((d) => d !== day)
	}

	selectedReminderDays.value.sort((a, b) => b - a)
}

function changesMade()
{
	const existingReminderDays = tenantSettings.value.passwords.reminder_email_days.value
	const reminderDaysMatch = selectedReminderDays.value.length === existingReminderDays.length && selectedReminderDays.value.every((val, index) => val === existingReminderDays[index])

	const toCompare = [
		{ current: selectedPasswordExpirationOption.value, previous: tenantSettings.value.passwords.expiration_enabled.value ? 'expire_days' : 'expire_never' },
		{ current: Number.parseInt(passwordExpirationDays.value), previous: Number.parseInt(tenantSettings.value.passwords.expiration_days.value) },
		{ current: Number.parseInt(loginWarningDays.value), previous: Number.parseInt(tenantSettings.value.passwords.login_warning_days.value) },
		{ current: Number.parseInt(passwordChangeDays.value), previous: Number.parseInt(tenantSettings.value.passwords.change_days.value) },
	]

	if (!reminderDaysMatch || toCompare.some((setting) => setting.current !== setting.previous))
	{
		showDiscardChangesModal.value = true
	}
	else
	{
		emit('dismiss', false)
	}
}
</script>

<template>
	<LoadingSpinner v-if="isLoading" />
	<div v-else>
		<div class="flex flex-col mb-6">
			<span class="text-xl font-semibold text-heading">Password Expiration</span>
			<span class="text-sm font-medium mb-5 text-heading">Require users to change passwords after a set number of days.</span>

			<div class="mb-5 flex flex-col space-y-2 text-sm text-caption">
				<span class="font-medium text-sm text-grayscale-body">Expiration</span>
				<label class="inline-flex cursor-pointer items-center gap-1">
					<input type="radio" value="expire_never" v-model="selectedPasswordExpirationOption" class="peer sr-only" />
					<!-- Unchecked -->
					<img src="@/assets/images/components/radio-outline.svg" class="peer-checked:hidden" alt="Passwords never expire" />

					<!-- Checked -->
					<img src="@/assets/images/components/radio-selected.svg" class="hidden peer-checked:block" alt="Passwords never expire" />
					<span>Passwords never expire</span>
				</label>

				<label class="inline-flex cursor-pointer items-center gap-1">
					<input type="radio" value="expire_days" v-model="selectedPasswordExpirationOption" class="peer sr-only" />
					<!-- Unchecked -->
					<img src="@/assets/images/components/radio-outline.svg" class="peer-checked:hidden" alt="Passwords expire after a set number of days" />

					<!-- Checked -->
					<img src="@/assets/images/components/radio-selected.svg" class="hidden peer-checked:block" alt="Passwords expire after a set number of days" />
					<span>Passwords expire after a set number of days</span>
				</label>
			</div>

			<div class="flex flex-col px-3 border-l-2 border-l-primary-subtle" :inert="isReadOnly">
				<span class="font-medium text-sm text-grayscale-body">Require a new password when it is older than</span>

				<div class="flex space-x-2 mt-2">
					<InputText class="w-fit !my-0" type="number" v-model="passwordExpirationDays" :disabled="isReadOnly" input-width="!w-24" input-height="!h-9.5"
					           :input-filter="(event) => nonEmptyNumberFilter(event, (val) => passwordExpirationDays = val)"
										 :before-input="(event) => beforeEditNumber(event, (val) => passwordExpirationDays = val)" />
					<span class="flex space-x-4">
						<span class="h-fit my-auto text-sm">{{ FormatUtils.pluralize('day', passwordExpirationDays) }}</span>
						<span class="expire-days" v-for="days in [30,60,90,180]" :key="days" @click.prevent="setExpirationDays(days)">{{days}} days</span>
					</span>
				</div>
				<ValidationError :errors="validation.errors" field="settings.passwords.expiration_days" class="mt-2 ml-1" />

				<span class="font-medium text-sm text-grayscale-body mt-5">Reminder emails, days before expiry</span>
				<fieldset class="flex space-x-5 mt-2">
					<CheckBox v-for="days in [14,7,3,1]" :key="days" :label="`${days} ${FormatUtils.pluralize('day', days)}`" :model-value="selectedReminderDays.includes(days)"
					          @update:model-value="(isChecked) => updateReminderDay(isChecked, days)" :input-id="'reminder-day-' + days.toString()" box-size-class="size-4" check-size-class="size-4"
										:disabled="isReadOnly"/>
				</fieldset>
				<span class="font-medium text-xs text-grayscale-body mt-2">Sent on these days. Users who change their password stop receiving reminders.</span>
				<ValidationError :errors="validation.errors" field="settings.passwords.reminder_email_days" class="mt-2 ml-1" />

				<span class="font-medium text-sm text-grayscale-body mt-5">Warn at log-in starting</span>
				<div class="flex space-x-2 mt-2">
					<InputText class="w-fit !my-0" type="number" v-model="loginWarningDays" :disabled="isReadOnly" input-width="!w-24" input-height="!h-9.5"
					           :input-filter="(event) => nonEmptyNumberFilter(event, (val) => loginWarningDays = val)"
										 :before-input="(event) => beforeEditNumber(event, (val) => loginWarningDays = val)" />
					<span class="h-fit my-auto text-sm text-caption">{{ FormatUtils.pluralize('day', loginWarningDays) }} before expiry</span>
				</div>
				<ValidationError :errors="validation.errors" field="settings.passwords.login_warning_days" class="mt-2 ml-1" />

				<span class="font-medium text-sm text-grayscale-body mt-5">When this policy is first turned on</span>
				<div class="flex space-x-2 mt-2">
					<span class="h-fit my-auto text-sm text-caption">Give users with older passwords</span>
					<InputText class="w-fit !my-0" type="number" v-model="passwordChangeDays" :disabled="isReadOnly" input-width="!w-24" input-height="!h-9.5"
					           :input-filter="(event) => nonEmptyNumberFilter(event, (val) => passwordChangeDays = val)"
										 :before-input="(event) => beforeEditNumber(event, (val) => passwordChangeDays = val)" />
					<span class="h-fit my-auto text-sm text-caption">{{ FormatUtils.pluralize('day', passwordChangeDays) }} to change it</span>
				</div>
				<ValidationError :errors="validation.errors" field="settings.passwords.change_days" class="mt-2 ml-1" />

				<div class="flex flex-col rounded-xl text-sm p-3 bg-primary-subtle mt-5 text-caption font-medium">
					<span>◦ Passwords older than {{ passwordExpirationDays }} {{ FormatUtils.pluralize('day', passwordExpirationDays) }} must be changed.</span>
					<span v-if="reminderDaysText">◦ Reminder emails go out {{ reminderDaysText }} day(s) before expiry.</span>
					<span>◦ The log-in warning starts {{ loginWarningDays }} {{ FormatUtils.pluralize('day', loginWarningDays) }} before expiry.</span>
					<span>◦ Users whose passwords are already older than {{ passwordExpirationDays }} {{ FormatUtils.pluralize('day', passwordExpirationDays) }} have {{ passwordChangeDays }} {{ FormatUtils.pluralize('day', passwordChangeDays) }} to change them.</span>
					<span class="font-semibold">◦ Applies to portal passwords only. Users who log in through single-sign on follow their identity provider's policy.</span>
				</div>
			</div>
		</div>

		<div class="flex w-full mt-4 space-x-4">
			<SubmitButton label="Save" :size="ButtonSize.Small" @click.prevent="save" :disabled="isSaving" :loading="isSaving" />
			<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="changesMade"
			              class="mt-3 sm:mt-0" :class="{'sm:mr-4': true}" />
		</div>
	</div>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDiscardChangesModal"
			icon="info"
			title="Discard Changes"
			message="Are you sure?"
			subMessage="Leaving now will discard any changes made."
			:buttons="[
				new ModalButtonData(
					'Cancel', () => { showDiscardChangesModal = false }, ButtonType.Outline, ButtonSize.Small
				),
				new ModalButtonData(
					'Yes', () => { showDiscardChangesModal = false; emit('dismiss', false) }, ButtonType.Default, ButtonSize.Small
				)
			]"
		/>
	</Teleport>
</template>

<style scoped>
.expire-days {
	@apply h-fit p-2 my-auto text-sm text-primary font-medium cursor-pointer rounded ring-0 bg-white select-none
			   hover:ring-1 hover:ring-neutral hover:bg-primary-subtlest
			   active:ring-[3px] active:ring-information-pressed active:bg-primary-subtlest
}
</style>